/**
 * LES RELANCES SUR LES FACTURES PENNYLANE, CÔTÉ ÉCRAN — lot 156, 03/11/2026.
 *
 * Les données viennent de `GET /relances-pl` (lot 155, `apps/api/src/relances_pl.ts`).
 * Ce fichier ne parle à personne : il range la file, compte ses chiffres et
 * écrit le TEXTE DES COURRIERS. Les textes sont ceux de la maquette validée
 * par Mohamed le 03/11 (planche « Recouvrement · 3 »), le pourcentage des
 * intérêts retiré à sa demande.
 */

export type NiveauPl =
  | 'avis'
  | 'relance1'
  | 'relance2'
  | 'relance3'
  | 'relance4'
  | 'mise_en_demeure';

export const NOMS_NIVEAUX: Readonly<Record<NiveauPl, string>> = {
  avis: 'Avis de compte',
  relance1: 'Relance 1',
  relance2: 'Relance 2',
  relance3: 'Relance 3',
  relance4: 'Relance 4',
  mise_en_demeure: 'Mise en demeure',
};

export interface ClientRelance {
  readonly id: string;
  readonly nom: string | null;
  readonly code: string | null;
  readonly courriel_facturation: string | null;
  readonly adresse: string | null;
  readonly code_postal: string | null;
  readonly ville: string | null;
  readonly pays: string | null;
  readonly siret: string | null;
}

export interface EnvoiPasse {
  readonly niveau: NiveauPl;
  readonly envoyee_le: string;
  readonly canal: string;
}

export interface AEnvoyer {
  readonly niveau: NiveauPl;
  readonly nom: string;
  readonly prevu_le: string;
  readonly groupe: 'retard' | 'jour';
  readonly taux: number | null;
  readonly interets: number;
  readonly indemnite: number;
  readonly montant_du: number;
}

export interface LigneRelance {
  readonly pennylane_facture_id: string;
  readonly numero: string | null;
  readonly libelle: string | null;
  readonly client: ClientRelance | null;
  readonly client_pennylane_id: string | null;
  readonly facture_stafa_id: string | null;
  readonly date_piece: string | null;
  readonly echeance: string;
  readonly montant_ttc: string;
  readonly reste_du: string;
  readonly retard_jours: number;
  readonly derniere: { readonly niveau: NiveauPl; readonly envoyee_le: string } | null;
  readonly envoyes: readonly EnvoiPasse[];
  readonly gel: { readonly motif: string; readonly reprise_le: string; readonly note: string | null } | null;
  readonly a_envoyer: AEnvoyer | null;
}

export interface FileRelances {
  readonly jour: string;
  readonly lignes: readonly LigneRelance[];
}

/** Les trois groupes de la file : en retard d'envoi, du jour, gelées. */
export function grouper(lignes: readonly LigneRelance[]): {
  readonly retard: readonly LigneRelance[];
  readonly jour: readonly LigneRelance[];
  readonly gelees: readonly LigneRelance[];
} {
  return {
    retard: lignes.filter((l) => l.gel === null && l.a_envoyer?.groupe === 'retard'),
    jour: lignes.filter((l) => l.gel === null && l.a_envoyer?.groupe === 'jour'),
    gelees: lignes.filter((l) => l.gel !== null),
  };
}

/** Les quatre chiffres en tête de la file. */
export function chiffres(lignes: readonly LigneRelance[], envoyeesCeMois: number) {
  const aEnvoyer = lignes.filter((l) => l.gel === null && l.a_envoyer !== null);
  return {
    a_envoyer: aEnvoyer.length,
    en_retard: aEnvoyer.filter((l) => l.a_envoyer?.groupe === 'retard').length,
    // EN CENTIMES pour additionner sans flottant traînant.
    montant_centimes: aEnvoyer.reduce((s, l) => s + Math.round(Number(l.reste_du) * 100), 0),
    envoyees_ce_mois: envoyeesCeMois,
  };
}

/** Les relances parties ce mois-ci, toutes factures de la file confondues. */
export function envoyeesCeMois(lignes: readonly LigneRelance[], jour: string): number {
  const mois = jour.slice(0, 7);
  return lignes.reduce((n, l) => n + l.envoyes.filter((e) => e.envoyee_le.slice(0, 7) === mois).length, 0);
}

/** « 2026-10-15 » → « 15/10/2026 ». */
export function dateDite(jour: string): string {
  return `${jour.slice(8, 10)}/${jour.slice(5, 7)}/${jour.slice(0, 4)}`;
}

/** « 2026-10-15 » → « 15/10/26 », pour le tableau du courrier. */
export function dateCourte(jour: string): string {
  return `${jour.slice(8, 10)}/${jour.slice(5, 7)}/${jour.slice(2, 4)}`;
}

/** Une date civile décalée de `n` jours. */
export function decaler(jour: string, n: number): string {
  const d = new Date(
    Date.UTC(Number(jour.slice(0, 4)), Number(jour.slice(5, 7)) - 1, Number(jour.slice(8, 10)) + n),
  );
  return d.toISOString().slice(0, 10);
}

/** « 2117.32 » → « 2 117,32 » — espace simple, lue partout. */
export function montantDit(montant: number): string {
  const [ent, dec] = montant.toFixed(2).split('.') as [string, string];
  return `${ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec}`;
}

/**
 * « 17/10, 23/10 et 03/11/2026 » : les relances déjà parties, l'avis de
 * compte exclu (il n'est pas une relance). `null` s'il n'y en a aucune.
 */
export function datesDesRelances(envoyes: readonly EnvoiPasse[]): string | null {
  const dates = envoyes.filter((e) => e.niveau !== 'avis').map((e) => e.envoyee_le);
  if (dates.length === 0) return null;
  const courte = (j: string) => `${j.slice(8, 10)}/${j.slice(5, 7)}`;
  if (dates.length === 1) return dateDite(dates[0] as string);
  const tete = dates.slice(0, -1).map(courte).join(', ');
  return `${tete} et ${dateDite(dates[dates.length - 1] as string)}`;
}

/**
 * LE TEXTE D'UN COURRIER, paragraphe par paragraphe — maquette validée le
 * 03/11. Les dates des relances précédentes et les délais sont calculés ;
 * quand aucune relance précédente n'est connue, la phrase le dit sans date.
 */
export function texteDuCourrier(
  niveau: NiveauPl,
  c: {
    readonly echeance: string;
    readonly envoyes: readonly EnvoiPasse[];
    readonly jour: string;
    readonly montantDu: number;
  },
): readonly string[] {
  const ech = dateDite(c.echeance);
  const avant = datesDesRelances(c.envoyes);
  switch (niveau) {
    case 'avis':
      return [
        `La facture ci-dessous arrive à échéance le ${ech}. Nous vous remercions de vérifier qu’elle est bien enregistrée dans votre comptabilité et programmée pour un règlement à cette date.`,
        'Si une difficulté empêche son paiement, merci de nous le signaler dès maintenant afin que nous puissions la régler ensemble.',
      ];
    case 'relance1':
      return [
        `Sauf erreur de notre part, la facture ci-dessous, échue le ${ech}, reste impayée à ce jour. Nous vous remercions de procéder à son règlement dans les meilleurs délais.`,
        'Nous vous rappelons que le délai de paiement des prestations de transport routier de marchandises ne peut excéder trente jours à compter de la date d’émission de la facture (article L. 441-11 du code de commerce).',
        'Si votre règlement est parti entre-temps, nous vous prions de ne pas tenir compte de ce courrier.',
      ];
    case 'relance2':
      return [
        `${avant === null ? 'Notre précédent courrier est resté sans suite' : `Notre courrier du ${avant} est resté sans suite`} : la facture ci-dessous, échue depuis le ${ech}, n’est toujours pas réglée. Nous vous demandons de procéder à son paiement sans délai.`,
        'À défaut, des intérêts de retard ainsi qu’une indemnité forfaitaire pour frais de recouvrement de 40 € par facture vous seront facturés, conformément à l’article L. 441-10 du code de commerce.',
      ];
    case 'relance3':
      return [
        `${avant === null ? 'Malgré nos relances' : `Malgré nos relances des ${avant}`}, la facture ci-dessous demeure impayée.`,
        'Les pénalités prévues par l’article L. 441-10 du code de commerce sont désormais dues : intérêts de retard au taux de refinancement de la BCE majoré de 10 points et indemnité forfaitaire de 40 € par facture. Le montant dû à ce jour figure ci-dessous.',
        'Nous vous demandons de le régler dans les plus brefs délais.',
      ];
    case 'relance4':
      return [
        `${avant === null ? 'En dépit de nos relances' : `En dépit de nos relances des ${avant}`}, la facture ci-dessous n’est toujours pas réglée.`,
        `Nous vous demandons de régler le montant dû indiqué ci-dessous au plus tard le ${dateDite(decaler(c.jour, 7))}. Les intérêts de retard continuent de courir jusqu’au complet paiement.`,
        'Ce courrier est notre dernière relance avant mise en demeure.',
      ];
    case 'mise_en_demeure':
      return [
        `${avant === null ? 'Malgré nos relances' : `Malgré nos relances des ${avant}`}, la facture ci-dessous demeure impayée.`,
        `Par la présente, nous vous mettons en demeure de nous régler la somme de ${montantDit(c.montantDu)} € détaillée ci-dessous dans un délai de cinq jours, soit au plus tard le ${dateDite(decaler(c.jour, 5))} (article 1344 du code civil).`,
        `À défaut de règlement dans ce délai, nous nous réservons, sans autre avis : 1. d’exercer l’action directe en paiement prévue par l’article L. 132-8 du code de commerce auprès de l’expéditeur ou du destinataire des marchandises ; 2. de confier le recouvrement de notre créance à un tiers, les frais engagés restant à votre charge ; 3. de suspendre nos prestations à compter du ${dateDite(decaler(c.jour, 6))}.`,
      ];
  }
}

/** Le nom du PDF joint : « Relance-3_FA26-0104_ETS-VASSEUR.pdf ». */
export function nomDuPdf(niveau: NiveauPl, numero: string | null, client: string | null): string {
  const propre = (t: string) =>
    t
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  const morceaux = [propre(NOMS_NIVEAUX[niveau]), propre(numero ?? 'facture')];
  if (client !== null && client.trim() !== '') morceaux.push(propre(client));
  return `${morceaux.join('_')}.pdf`;
}

// ---------------------------------------------------------------------------
// LA RECHERCHE, LES FILTRES ET LE TRI — lot 157, 03/11/2026. Demande de
// Mohamed : l'écran des relances « comme la liste des OT, dynamique » —
// recherche, panneau Filtrer, tri par colonne.
// ---------------------------------------------------------------------------

export type EtatFile = 'retard' | 'jour' | 'gelee';
export type AdresseFile = 'avec' | 'sans';

export interface FiltresRelances {
  readonly niveaux: readonly NiveauPl[];
  readonly etats: readonly EtatFile[];
  readonly adresses: readonly AdresseFile[];
}

export const AUCUN_FILTRE_RELANCES: FiltresRelances = { niveaux: [], etats: [], adresses: [] };

/** Combien de cases sont cochées, pour le bouton « Filtrer · n ». */
export function combienDeFiltresRelances(f: FiltresRelances): number {
  return f.niveaux.length + f.etats.length + f.adresses.length;
}

/** Une case cochée ou décochée. */
export function basculerFiltre<K extends keyof FiltresRelances>(
  f: FiltresRelances,
  cle: K,
  valeur: FiltresRelances[K][number],
): FiltresRelances {
  const liste = f[cle] as readonly string[];
  const suivante = liste.includes(valeur) ? liste.filter((v) => v !== valeur) : [...liste, valeur];
  return { ...f, [cle]: suivante };
}

export function etatDeLaLigne(l: LigneRelance): EtatFile | null {
  if (l.gel !== null) return 'gelee';
  if (l.a_envoyer === null) return null;
  return l.a_envoyer.groupe;
}

/** Sans accent ni majuscule, pour comparer ce qu'on tape à ce qu'on lit. */
function plat(t: string): string {
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * LA RECHERCHE ET LES FILTRES. Même règle que la liste des OT : « OU » entre
 * les cases d'un même critère, « ET » entre les critères ; un critère sans
 * case cochée ne filtre rien. La recherche porte sur le client, le numéro de
 * facture et l'adresse mail.
 */
export function filtrerRelances(
  lignes: readonly LigneRelance[],
  f: FiltresRelances,
  recherche: string,
): readonly LigneRelance[] {
  const mots = plat(recherche).split(/\s+/).filter((m) => m !== '');
  return lignes.filter((l) => {
    const niveau = l.gel !== null ? null : (l.a_envoyer?.niveau ?? null);
    if (f.niveaux.length > 0 && (niveau === null || !f.niveaux.includes(niveau))) return false;
    const etat = etatDeLaLigne(l);
    if (f.etats.length > 0 && (etat === null || !f.etats.includes(etat))) return false;
    const adresse: AdresseFile = (l.client?.courriel_facturation ?? null) === null ? 'sans' : 'avec';
    if (f.adresses.length > 0 && !f.adresses.includes(adresse)) return false;
    if (mots.length === 0) return true;
    const texte = plat(
      [l.client?.nom ?? '', l.libelle ?? '', l.numero ?? '', l.client?.courriel_facturation ?? ''].join(' '),
    );
    return mots.every((m) => texte.includes(m));
  });
}

export type ColonneRelances = 'client' | 'numero' | 'echeance' | 'retard' | 'reste' | 'niveau';
export interface TriRelances {
  readonly colonne: ColonneRelances;
  readonly sens: 'croissant' | 'decroissant';
}

/** Le tri par défaut : le plus en retard d'abord. */
export const TRI_RELANCES: TriRelances = { colonne: 'retard', sens: 'decroissant' };

const RANG_NIVEAU: Readonly<Record<NiveauPl, number>> = {
  avis: 0,
  relance1: 1,
  relance2: 2,
  relance3: 3,
  relance4: 4,
  mise_en_demeure: 5,
};

/** Un clic sur une colonne : la même inverse le sens, une autre commence croissant. */
export function basculerTri(tri: TriRelances, colonne: ColonneRelances): TriRelances {
  return tri.colonne === colonne
    ? { colonne, sens: tri.sens === 'croissant' ? 'decroissant' : 'croissant' }
    : { colonne, sens: 'croissant' };
}

export function trierRelances(lignes: readonly LigneRelance[], tri: TriRelances): readonly LigneRelance[] {
  const cle = (l: LigneRelance): string | number => {
    switch (tri.colonne) {
      case 'client':
        return plat(l.client?.nom ?? l.libelle ?? '');
      case 'numero':
        return l.numero ?? '';
      case 'echeance':
        return l.echeance;
      case 'retard':
        return l.retard_jours;
      case 'reste':
        return Number(l.reste_du);
      case 'niveau':
        return l.a_envoyer === null ? -1 : RANG_NIVEAU[l.a_envoyer.niveau];
    }
  };
  const signe = tri.sens === 'croissant' ? 1 : -1;
  // UN TRI STABLE : à égalité, l'ordre de l'API (échéance, numéro) est gardé.
  return lignes
    .map((l, i) => ({ l, i }))
    .sort((a, b) => {
      const x = cle(a.l);
      const y = cle(b.l);
      if (x < y) return -signe;
      if (x > y) return signe;
      return a.i - b.i;
    })
    .map((e) => e.l);
}
